import { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import { createAxiosInstance } from "api/axiosConfig";
import { usePCRSCredentials } from "features/cdm/hooks/usePCRSCredentials";
import { useDeadLetters } from "features/healthlink/hooks/useDeadLetters";
import { useHealthlinkCalls } from "features/healthlink/hooks/useHealthlinkCalls";
import { useHealthlinkCredentials } from "features/healthlink/hooks/useHealthlinkCredentials";
import { useHealthlinkPractice } from "features/healthlink/hooks/useHealthlinkPractice";
import { useHealthlinkPracticeCredentials } from "features/healthlink/hooks/useHealthlinkPracticeCredentials";
import { useCodeTables } from "hooks/useCodeTables";
import { useSyncCodeTables } from "hooks/useSyncCodeTables";
import { showMutationError } from "utils/showMutationError";

const CODE_TABLE_DEFAULT_ROWS = 10;

export const TABS = {
  credentials: "credentials",
  calls: "log",
  codeTable: "codeTable",
  deadLetters: "deadLetters",
};

export const useHealthlinkDetailsPage = () => {
  const queryClient = useQueryClient();
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const [activeTab, setActiveTab] = useState(TABS.credentials);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [deadLetterPage, setDeadLetterPage] = useState(1);
  const [deadLetterRows, setDeadLetterRows] = useState(10);

  const [codeTablePage, setCodeTablePage] = useState(1);
  const [codeTableRows, setCodeTableRows] = useState(CODE_TABLE_DEFAULT_ROWS);
  const [codeTableSearch, setCodeTableSearch] = useState("");

  const { data: healthlinkPractice } = useHealthlinkPractice();
  const { data: healthlinkCredentials } = useHealthlinkCredentials();
  const { data: healthlinkPracticeCredentials } =
    useHealthlinkPracticeCredentials();
  const { data: pcrsCredentials } = usePCRSCredentials();

  const { data: healthlinkCallsData, isLoading: isLoadingCalls } =
    useHealthlinkCalls({
      page: currentPage,
      rows: rowsPerPage,
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
      enabled: activeTab === TABS.calls,
    });

  const { data: codeTablesData, isLoading: isLoadingCodeTables } =
    useCodeTables({
      page: codeTablePage,
      rows: codeTableRows,
      searchTerm: codeTableSearch || null,
      enabled: activeTab === TABS.codeTable,
    });

  // Fetched whichever tab is open: the count is what makes an unparseable message
  // discoverable, and a tab nobody opens would never show one.
  const { data: deadLettersData, isLoading: isLoadingDeadLetters } =
    useDeadLetters({ page: deadLetterPage, size: deadLetterRows });

  const syncCodeTablesMutation = useSyncCodeTables();

  const handleSyncCodeTables = () => {
    syncCodeTablesMutation.mutate(null, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["codeTables"] });
      },
    });
  };

  const { mutate: deletePracticeCredential } = useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (id) => {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.delete(
        `${tenantVal}/healthlink/credentials/practice/delete/${id}`
      );
    },
    onSuccess: () => {
      toast.success("Practice credential deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["healthlinkPracticeCredentials"],
      });
    },
    onError: (error) =>
      showMutationError(error, "Failed to delete practice credential"),
  });

  const { mutate: deleteDoctorCredential } = useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (id) => {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.delete(
        `${tenantVal}/healthlink/credentials/delete/${id}`
      );
    },
    onSuccess: () => {
      toast.success("Doctor credential deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["healthMailLoginDetails"] });
    },
    onError: (error) =>
      showMutationError(error, "Failed to delete doctor credential"),
  });

  const { mutate: testLogin, isLoading: isTestingLogin } = useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (staffId) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/healthlink/test-login/${staffId}`
      );

      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Successful Test Login Response: ${data}`);
    },
    onError: (error) => showMutationError(error, "Test login failed"),
  });

  const { mutate: syncVaccineCodes, isLoading: isSyncingVaccineCodes } =
    useMutation({
      meta: { skipGlobalError: true },
      mutationFn: async () => {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${tenantVal}/vaccine-codes/sync`
        );

        return response.data;
      },
      onSuccess: () => {
        toast.success("Vaccine codes synced successfully from Healthlink");
      },
      onError: (error) =>
        showMutationError(error, "Failed to sync vaccine codes"),
    });

  const hasClinicDetailsSaved =
    Array.isArray(healthlinkCredentials) && healthlinkCredentials.length > 0;

  const hasGpDetailsSaves = !!healthlinkPractice && hasClinicDetailsSaved;

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setCurrentPage(1);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setCurrentPage(1);
  };

  const clearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const handleCodeTableSearchChange = (value) => {
    setCodeTableSearch(value);
    setCodeTablePage(1);
  };

  const handleCodeTableRowsChange = (rows) => {
    setCodeTableRows(rows);
    setCodeTablePage(1);
  };

  const clearCodeTableSearch = () => {
    setCodeTableSearch("");
    setCodeTablePage(1);
  };

  const handleDeadLetterRowsChange = (rows) => {
    setDeadLetterRows(rows);
    setDeadLetterPage(1);
  };

  return {
    activeTab,
    setActiveTab,
    healthlinkPractice,
    healthlinkCredentials,
    healthlinkPracticeCredentials,
    pcrsCredentials,
    hasClinicDetailsSaved,
    hasGpDetailsSaves,
    deletePracticeCredential,
    deleteDoctorCredential,
    testLogin,
    isTestingLogin,
    syncVaccineCodes,
    isSyncingVaccineCodes,
    calls: {
      data: healthlinkCallsData?.data || [],
      totalRecords: healthlinkCallsData?.totalRecords || 0,
      isLoading: isLoadingCalls,
      currentPage,
      rowsPerPage,
      startDate,
      endDate,
      onPageChange: setCurrentPage,
      onRowsChange: handleRowsPerPageChange,
      onStartDateChange: handleStartDateChange,
      onEndDateChange: handleEndDateChange,
      onClearDateFilters: clearDateFilters,
    },
    deadLetters: {
      data: deadLettersData?.results || [],
      total: deadLettersData?.total || 0,
      isLoading: isLoadingDeadLetters,
      currentPage: deadLetterPage,
      rowsPerPage: deadLetterRows,
      onPageChange: setDeadLetterPage,
      onRowsChange: handleDeadLetterRowsChange,
    },
    codeTable: {
      data: codeTablesData?.data || [],
      totalRecords: codeTablesData?.totalRecords || 0,
      isLoading: isLoadingCodeTables,
      currentPage: codeTablePage,
      rowsPerPage: codeTableRows,
      search: codeTableSearch,
      isSyncing: syncCodeTablesMutation.isPending,
      onPageChange: setCodeTablePage,
      onRowsChange: handleCodeTableRowsChange,
      onSearchChange: handleCodeTableSearchChange,
      onClearSearch: clearCodeTableSearch,
      onSync: handleSyncCodeTables,
    },
  };
};
